import React, { useEffect, useState } from 'react';
import { Container, Card, Button, Badge, Table, Row, Col, Alert, Modal } from 'react-bootstrap';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faList } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import { formatDate, calculateDaysDifference } from '../utils/dateFormatter';

const ViewSample = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { testRequestId } = useParams();
  const [testRequest, setTestRequest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [observationsCompleted, setObservationsCompleted] = useState(false);
  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);
  const [selectedTestForEdit, setSelectedTestForEdit] = useState(null);
  
  // Debug: Log the received data
  console.log('ViewSample - location.state:', location.state);
  console.log('ViewSample - formData:', location.state?.formData);
  console.log('ViewSample - pathname:', location.pathname);
  console.log('ViewSample - search:', location.search);
  
  useEffect(() => {
    const fetchTestRequestData = async () => {
      // If testRequestId is in URL, fetch from API
      if (testRequestId) {
        setLoading(true);
        try {
          const response = await axios.get(`${API_BASE_URL}/test-requests/${testRequestId}/details`);
          const data = response.data;
          
          // Build testRequest object from API data
          
          const newTestRequest = {
            id: parseInt(testRequestId),
            customerId: data.customer?.id || null,
            customerName: data.customer?.name || 'N/A',
            phone: data.customer?.phone || 'N/A',
            siteName: data.test_request?.site_name || 'N/A',
            siteAddress: data.customer?.address || 'N/A',
            testType: data.test_request?.test_type || 'CC',
            receiptDate: data.test_request?.receipt_date || 'N/A',
            receipt_date: data.test_request?.receipt_date || 'N/A', // Also include underscore version for compatibility
            ulrNumber: data.test_request?.ulr_number || 'N/A',
            referenceNumber: data.test_request?.sample_code_number || data.concrete_tests?.[0]?.sample_code_number || 'N/A',
            jobNumber: data.test_request?.job_number || 'N/A',
            cubeTests: data.concrete_tests?.map((ct, index) => {
              return {
                id: ct.id || (index + 1),
                idMark: ct.idMark,
                locationNature: ct.locationNature,
                grade: ct.grade,
                castingDate: ct.casting_date ? formatDate(ct.casting_date) : (ct.castingDate || 'N/A'),
                testingDate: ct.testing_date ? formatDate(ct.testing_date) : (ct.testingDate || 'N/A'),
                quantity: ct.quantity,
                testMethod: ct.testMethod,
                sample_code_number: ct.sample_code_number
              };
            }) || []
          };
          
          
          setTestRequest(newTestRequest);
          
          // Check if observations are completed (if any concrete test has results)
          const hasObservations = data.concrete_tests?.some(ct => ct.has_results || ct.observations_completed);
          setObservationsCompleted(hasObservations);
        } catch (error) {
          console.error('Error fetching test request:', error);
          setTestRequest(null);
        } finally {
          setLoading(false);
        }
      } else {
        // Fallback to location.state
        const newTestRequest = location.state?.formData;
        setTestRequest(newTestRequest);
        console.log('ViewSample - Using location.state:', newTestRequest);
      }
    };
    
    fetchTestRequestData();
  }, [testRequestId, location.state, location.pathname]);

  // Show skeleton loading state if testRequest is not loaded yet
  if (loading || !testRequest) {
    return (
      <Container className="mt-4">
        <style>
          {`
            @keyframes skeleton-pulse {
              0% { opacity: 1; }
              50% { opacity: 0.4; }
              100% { opacity: 1; }
            }
            .skeleton {
              background: linear-gradient(90deg, #2d3748 25%, #3a4553 50%, #2d3748 75%);
              background-size: 200% 100%;
              animation: skeleton-pulse 1.5s ease-in-out infinite;
              border-radius: 4px;
            }
          `}
        </style>
        <Row className="mb-4">
          <Col>
            <Card className="shadow">
              <Card.Header style={{ backgroundColor: '#FFA500', padding: '20px' }}>
                <div className="skeleton" style={{ height: '60px', width: '100%' }}></div>
              </Card.Header>
              <Card.Body>
                <Row className="mb-4">
                  <Col md={6}>
                    <div className="skeleton mb-2" style={{ height: '30px', width: '60%' }}></div>
                    <div className="skeleton mb-2" style={{ height: '150px', width: '100%' }}></div>
                  </Col>
                  <Col md={6}>
                    <div className="skeleton mb-2" style={{ height: '30px', width: '60%' }}></div>
                    <div className="skeleton mb-2" style={{ height: '150px', width: '100%' }}></div>
                  </Col>
                </Row>
                <div className="skeleton mb-2" style={{ height: '30px', width: '50%' }}></div>
                <div className="skeleton" style={{ height: '200px', width: '100%' }}></div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-3 mt-md-4 px-2 px-md-3">
      {/* Navigation Header */}
      <Row className="mb-4">
        <Col>
        <Card className="shadow-lg" style={{ border: 'none', borderRadius: '15px', overflow: 'hidden' }}>
          <Card.Header className="text-white d-flex align-items-center" style={{ 
            backgroundColor: 'linear-gradient(135deg, #FFA500 0%, #FFA500 100%)',
            background: 'linear-gradient(135deg, #FFA500 0%, #FFA500 100%)',
            border: 'none',
            padding: '20px 25px'
          }}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <img 
                    src="/logo.png" 
                    alt="Vitrag Associates Logo" 
                    height="40" 
                    className="me-2 d-none d-sm-block"
                  />
                  <div>
                    <h3 className="mb-0 h5 h-md-3">
                      <span style={{ color: '#FFD700' }}>Vitrag Associates LLP</span>
                    </h3>
                    <h4 className="mt-1 h6 h-md-4">Sample Details - {testRequest.jobNumber}</h4>
                  </div>
                </div>
              </div>
            </Card.Header>
          </Card>
        </Col>
      </Row>
      <style>{`
        .table th {
          background-color: #6c757d !important;
          color: white !important;
        }
        .table td {
          background-color: #1C2333 !important;
          color: white !important;
        }
        .bg-secondary {
          background-color: #6c757d !important;
        }
        .bg-dark {
          background-color: #1C2333 !important;
        }
      `}</style>

      <Card className="shadow mb-4">
        <Card.Header className="text-white d-flex justify-content-between align-items-center" style={{ backgroundColor: '#FFA500' }}>
          <div className="d-flex align-items-center">
            <img 
              src="/logo.png" 
              alt="Vitrag Associates Logo" 
              height="50" 
              className="me-3"
            />
            <div>
              <h3 className="mb-0">
                <span style={{ color: '#FFD700' }}>Vitrag Associates LLP</span> - Sample Test Request
              </h3>
              <div className="mt-2">
                <Badge style={{ backgroundColor: '#FFA500' }} className="fs-6 p-2">
                  Job Number: {testRequest.jobNumber}
                </Badge>
              </div>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Button 
              variant="light" 
              size="sm" 
              className="px-3 py-2"
              onClick={() => {
                // Navigate to edit test request form with current data
                navigate('/test-request', {
                  state: {
                    editMode: true,
                    testRequestData: testRequest
                  }
                });
              }}
              style={{
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.05)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 255, 255, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <i className="fas fa-edit"></i> Edit
            </Button>
            {observationsCompleted ? (
              <>
                <Button 
                  variant="success" 
                  size="sm" 
                  className="px-3 py-2 me-2"
                  onClick={() => {
                    const reqId = testRequestId || testRequest?.id;
                    navigate('/strength-graph', {
                      state: {
                        formData: testRequest,
                        testData: testRequest.cubeTests?.[0],
                        testIndex: 0,
                        testRequestId: reqId
                      }
                    });
                  }}
                  style={{
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(40, 167, 69, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <i className="fas fa-chart-bar"></i> View Graph
                </Button>
                <Button 
                  variant="info" 
                  size="sm" 
                  className="px-3 py-2"
                  onClick={() => {
                    setSelectedTestForEdit({ test: testRequest.cubeTests?.[0], index: 0 });
                    setShowEditConfirmModal(true);
                  }}
                  style={{
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(23, 162, 184, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <i className="fas fa-edit"></i> Edit Observations
                </Button>
              </>
            ) : (
              <Button 
                variant="warning" 
                size="sm" 
                className="px-3 py-2"
                onClick={() => {
                  const reqId = testRequestId || testRequest?.id;
                  if (!reqId) {
                    alert('Error: Test Request ID is missing! Please navigate to this page from Dashboard or Samples to get a valid test request ID.');
                    return;
                  }
                  navigate(`/test-observations/${reqId}`, {
                    state: {
                      testRequest: { ...testRequest, id: parseInt(reqId) },
                      testData: testRequest.cubeTests?.[0],
                      testIndex: 0
                    }
                  });
                }}
                style={{
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 193, 7, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <i className="fas fa-plus"></i> {observationsCompleted ? 'Edit Observations' : 'Enter Observations'}
              </Button>
            )}
          </div>
        </Card.Header>
        
        <Card.Body style={{ padding: '30px' }}>
          <Row className="g-4 mb-4">
            <Col xs={12} md={6}>
              <Card className="shadow-lg h-100" style={{ border: 'none', borderRadius: '15px', overflow: 'hidden' }}>
                <Card.Header className="text-white d-flex align-items-center" style={{
                  backgroundColor: 'linear-gradient(135deg, #FFA500 0%, #FFA500 100%)',
                  background: 'linear-gradient(135deg, #FFA500 0%, #FFA500 100%)',
                  border: 'none',
                  padding: '20px 25px'
                }}>
                  <div className="d-flex align-items-center">
                    <div style={{
                      width: '45px',
                      height: '45px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px'
                    }}>
                      <i className="fas fa-user" style={{ fontSize: '20px' }}></i>
                    </div>
                    <div>
                      <h4 className="mb-0" style={{ fontWeight: '600', fontSize: '1.2rem' }}>Customer Information</h4>
                      <small style={{ opacity: '0.9', fontSize: '0.9rem' }}>Customer details and contact information</small>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body style={{ 
                  backgroundColor: '#1a1d29', 
                  padding: '25px',
                  border: '1px solid #2d3748',
                  minHeight: '280px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="d-flex align-items-center mb-3">
                        <i className="fas fa-building me-3" style={{ color: '#FFA500', fontSize: '18px' }}></i>
                        <div>
                          <div style={{ color: '#adb5bd', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Name of Customer</div>
                          <div style={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: '500' }}>{testRequest.customerName}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-phone me-3" style={{ color: '#FFA500', fontSize: '18px' }}></i>
                        <div>
                          <div style={{ color: '#adb5bd', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Phone/Mobile</div>
                          <div style={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: '500' }}>{testRequest.phone}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            
            <Col xs={12} md={6}>
              <Card className="shadow-lg h-100" style={{ border: 'none', borderRadius: '15px', overflow: 'hidden' }}>
                <Card.Header className="text-white d-flex align-items-center" style={{
                  backgroundColor: 'linear-gradient(135deg, #FFA500 0%, #FFA500 100%)',
                  background: 'linear-gradient(135deg, #FFA500 0%, #FFA500 100%)',
                  border: 'none',
                  padding: '20px 25px'
                }}>
                  <div className="d-flex align-items-center">
                    <div style={{
                      width: '45px',
                      height: '45px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px'
                    }}>
                      <i className="fas fa-flask" style={{ fontSize: '20px' }}></i>
                    </div>
                    <div>
                      <h4 className="mb-0" style={{ fontWeight: '600', fontSize: '1.2rem' }}>Sample Information</h4>
                      <small style={{ opacity: '0.9', fontSize: '0.9rem' }}>Test sample details and specifications</small>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body style={{ 
                  backgroundColor: '#1a1d29', 
                  padding: '25px',
                  border: '1px solid #2d3748',
                  minHeight: '280px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="d-flex align-items-center mb-3">
                        <i className="fas fa-calendar-alt me-3" style={{ color: '#FFA500', fontSize: '18px' }}></i>
                        <div>
                          <div style={{ color: '#adb5bd', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Receipt Date</div>
                          <div style={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: '500' }}>{testRequest.receiptDate}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center mb-3">
                        <i className="fas fa-map-marker-alt me-3" style={{ color: '#FFA500', fontSize: '18px' }}></i>
                        <div>
                          <div style={{ color: '#adb5bd', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Site Name</div>
                          <div style={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: '500' }}>{testRequest.siteName}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center mb-3">
                        <i className="fas fa-map-pin me-3" style={{ color: '#FFA500', fontSize: '18px' }}></i>
                        <div>
                          <div style={{ color: '#adb5bd', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Site Address</div>
                          <div style={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: '500' }}>{testRequest.siteAddress}</div>
                        </div>
                      </div>
                    </div>
                    <div className="col-12">
                      <div className="d-flex align-items-center">
                        <i className="fas fa-cogs me-3" style={{ color: '#FFA500', fontSize: '18px' }}></i>
                        <div>
                          <div style={{ color: '#adb5bd', fontSize: '0.9rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Test Type</div>
                          <div style={{ color: '#e2e8f0', fontSize: '1.1rem', fontWeight: '500' }}>{testRequest.testType}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          
          {testRequest.cubeTests && testRequest.cubeTests.length > 0 && (
            <>
              <Card className="shadow-lg mb-4" style={{ border: 'none', borderRadius: '15px', overflow: 'hidden' }}>
                <Card.Header className="text-white d-flex align-items-center" style={{
                  backgroundColor: 'linear-gradient(135deg, #FFA500 0%, #FFA500 100%)',
                  background: 'linear-gradient(135deg, #FFA500 0%, #FFA500 100%)',
                  border: 'none',
                  padding: '20px 25px'
                }}>
                  <div className="d-flex align-items-center">
                    <div style={{
                      width: '45px',
                      height: '45px',
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '15px'
                    }}>
                      <i className="fas fa-cube" style={{ fontSize: '20px' }}></i>
                    </div>
                    <div>
                      <h4 className="mb-0" style={{ fontWeight: '600', fontSize: '1.2rem' }}>Testing Requirement (Concrete Cube/Core)</h4>
                      <small style={{ opacity: '0.9', fontSize: '0.9rem' }}>Concrete test specifications and requirements</small>
                    </div>
                  </div>
                </Card.Header>
                <Card.Body style={{ 
                  backgroundColor: '#1a1d29', 
                  padding: '25px',
                  border: '1px solid #2d3748'
                }}>
                  <div className="table-responsive" style={{ 
                    borderRadius: '10px', 
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    overflowX: 'auto',
                    maxHeight: '500px'
                  }}>
                    <Table className="mb-0" style={{
                      backgroundColor: '#2d3748',
                      color: '#e2e8f0',
                      minWidth: '1200px'
                    }}>
                      <thead style={{
                        backgroundColor: '#1a202c',
                        borderBottom: '2px solid #FFA500'
                      }}>
                        <tr>
                          <th style={{ 
                            color: '#FFA500', 
                            fontWeight: '600', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '15px 10px',
                            border: 'none',
                            borderRight: '1px solid #4a5568',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            minWidth: '120px'
                          }}>Casting Date</th>
                          <th style={{ 
                            color: '#FFA500', 
                            fontWeight: '600', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '15px 10px',
                            border: 'none',
                            borderRight: '1px solid #4a5568',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            minWidth: '120px'
                          }}>Testing Date</th>
                          <th style={{ 
                            color: '#FFA500', 
                            fontWeight: '600', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '15px 10px',
                            border: 'none',
                            borderRight: '1px solid #4a5568',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            minWidth: '100px'
                          }}>Age in days</th>
                          <th style={{ 
                            color: '#FFA500', 
                            fontWeight: '600', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '15px 10px',
                            border: 'none',
                            borderRight: '1px solid #4a5568',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            minWidth: '120px'
                          }}>No of cubes/Cores</th>
                          <th style={{ 
                            color: '#FFA500', 
                            fontWeight: '600', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '15px 10px',
                            border: 'none',
                            borderRight: '1px solid #4a5568',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            minWidth: '200px'
                          }}>Test Method/Specification</th>
                          <th style={{ 
                            color: '#FFA500', 
                            fontWeight: '600', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '15px 10px',
                            border: 'none',
                            borderRight: '1px solid #4a5568',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            minWidth: '150px'
                          }}>Reference Number</th>
                          <th style={{ 
                            color: '#FFA500', 
                            fontWeight: '600', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '15px 10px',
                            border: 'none',
                            borderRight: '1px solid #4a5568',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            minWidth: '200px'
                          }}>ULR Number</th>
                          <th style={{ 
                            color: '#FFA500', 
                            fontWeight: '600', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '15px 10px',
                            border: 'none',
                            borderRight: '1px solid #4a5568',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            minWidth: '150px'
                          }}>Job Number</th>
                          <th style={{ 
                            color: '#FFA500', 
                            fontWeight: '600', 
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            padding: '15px 10px',
                            border: 'none',
                            fontSize: '0.9rem',
                            whiteSpace: 'nowrap',
                            minWidth: '120px'
                          }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testRequest.cubeTests.map((test, index) => (
                          <tr key={test.id} style={{
                            borderBottom: '1px solid #4a5568',
                            transition: 'background-color 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.target.closest('tr').style.backgroundColor = '#374151'}
                          onMouseLeave={(e) => e.target.closest('tr').style.backgroundColor = 'transparent'}
                          >
                             <td style={{ padding: '12px 10px', border: 'none', borderRight: '1px solid #4a5568', color: '#e2e8f0', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{test.castingDate}</td>
                             <td style={{ padding: '12px 10px', border: 'none', borderRight: '1px solid #4a5568', color: '#e2e8f0', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{test.testingDate}</td>
                             <td style={{ padding: '12px 10px', border: 'none', borderRight: '1px solid #4a5568', color: '#e2e8f0', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                          {test.castingDate && test.testingDate ? 
                            calculateDaysDifference(test.castingDate, test.testingDate) : 
                            'None'
                          }
                        </td>
                             <td style={{ padding: '12px 10px', border: 'none', borderRight: '1px solid #4a5568', color: '#e2e8f0', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{test.quantity}</td>
                             <td style={{ padding: '12px 10px', border: 'none', borderRight: '1px solid #4a5568', color: '#e2e8f0', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{test.testMethod}</td>
                             <td style={{ padding: '12px 10px', border: 'none', borderRight: '1px solid #4a5568', color: '#e2e8f0', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{testRequest.referenceNumber || test.sample_code_number || 'None'}</td>
                             <td style={{ padding: '12px 10px', border: 'none', borderRight: '1px solid #4a5568', color: '#e2e8f0', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{testRequest.ulrNumber || 'None'}</td>
                             <td style={{ padding: '12px 10px', border: 'none', borderRight: '1px solid #4a5568', color: '#e2e8f0', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>{testRequest.jobNumber}</td>
                             <td style={{ padding: '12px 10px', border: 'none', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <div className="d-flex gap-2 justify-content-center">
                            {test.has_results ? (
                              <>
                                {/* View Graph Button */}
                                <Button 
                                  variant="success" 
                                  size="sm"
                                  style={{ whiteSpace: 'nowrap', fontSize: '13px' }}
                                  onClick={() => {
                                    const reqId = testRequestId || testRequest?.id;
                                    navigate('/strength-graph', {
                                      state: {
                                        formData: testRequest,
                                        testData: test,
                                        testIndex: index,
                                        testRequestId: reqId
                                      }
                                    });
                                  }}
                                >
                                  <i className="fas fa-chart-bar"></i> View Graph
                                </Button>
                                
                                {/* Edit Observations Button */}
                                <Button 
                                  variant="info" 
                                  size="sm"
                                  style={{ whiteSpace: 'nowrap', fontSize: '13px' }}
                                  onClick={() => {
                                    setSelectedTestForEdit({ test, index });
                                    setShowEditConfirmModal(true);
                                  }}
                                >
                                  <i className="fas fa-edit"></i> Edit
                                </Button>
                              </>
                            ) : (
                              /* Enter Observations Button - Only shown when no data AND not observations_completed */
                              !observationsCompleted && (
                                <Button 
                                  variant="warning" 
                                  size="sm"
                                  style={{ whiteSpace: 'nowrap', fontSize: '13px' }}
                                  onClick={() => {
                                    const reqId = testRequestId || testRequest?.id;
                                    if (!reqId) {
                                      alert('Error: Test Request ID is missing! Please navigate to this page from Dashboard or Samples to get a valid test request ID.');
                                      return;
                                    }
                                    navigate(`/test-observations/${reqId}`, {
                                      state: {
                                        testRequest: { ...testRequest, id: parseInt(reqId) },
                                        testData: test,
                                        testIndex: index
                                      }
                                    });
                                  }}
                                >
                                  <i className="fas fa-plus"></i> Enter Observations
                                </Button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
                </Card.Body>
              </Card>
            </>
          )}
          
        </Card.Body>
      </Card>


      {/* Edit Confirmation Modal */}
      <Modal show={showEditConfirmModal} onHide={() => setShowEditConfirmModal(false)} centered>
        <Modal.Header closeButton style={{ backgroundColor: '#ffc107', borderBottom: 'none' }}>
          <Modal.Title style={{ color: '#000' }}>
            <i className="fas fa-exclamation-triangle me-2"></i>Confirm Edit
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#1a1a1a', color: '#fff', textAlign: 'center', padding: '30px' }}>
          <i className="fas fa-edit fa-3x mb-3" style={{ color: '#ffc107' }}></i>
          <h5>Are you sure you want to edit observations?</h5>
          <p className="text-muted mt-3">
            You are about to modify previously saved observation data. This action will allow you to change the test results.
          </p>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#2c2c2c', borderTop: '1px solid #444' }}>
          <Button variant="outline-light" onClick={() => setShowEditConfirmModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={() => {
              const reqId = testRequestId || testRequest?.id;
              if (!reqId || !selectedTestForEdit) {
                alert('Error: Test Request ID is missing!');
                return;
              }
              setShowEditConfirmModal(false);
              navigate(`/test-observations/${reqId}`, {
                state: {
                  testRequest: { ...testRequest, id: parseInt(reqId) },
                  testData: selectedTestForEdit.test,
                  testIndex: selectedTestForEdit.index,
                  editMode: true
                }
              });
            }}
          >
            <i className="fas fa-edit me-2"></i>Yes, Edit Observations
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ViewSample;
